import { Component, type ReactNode } from "react";

class PageImportError extends Error {}

// Only root page imports can trigger automatic recovery. A later workspace
// dialog/import failure must not automatically reload an active chat draft.
export async function loadPage<T>(load: () => Promise<T>): Promise<T> {
  try {
    return await load();
  } catch (cause) {
    throw new PageImportError("Unable to load page", { cause });
  }
}

export class PageLoadBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error: Error) {
    if (!(error instanceof PageImportError)) return;
    try {
      // This module belongs to the hashed entry bundle. Retry each entry at
      // most once per tab, including across reloads and persistent outages.
      const key = `truth-seeker:page-reload:${import.meta.url}`;
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, "1");
    } catch {
      // Storage can be unavailable in privacy modes: keep manual recovery.
      return;
    }
    window.location.reload();
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return <main className="auth-shell">
      <section className="auth-panel" role="alert">
        <h1>页面暂时无法加载</h1>
        <p>请检查网络连接，然后刷新页面重试。</p>
        <button className="primary-action" type="button" onClick={() => window.location.reload()}>刷新页面</button>
      </section>
    </main>;
  }
}
